import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import {
  Badge,
  Button,
  Checkbox,
  InlineError,
  Input,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { decideComponent, loadReview, reviewErrorMessage, signOff } from './reviewApi';
import {
  allApproved,
  buildSlots,
  canSignOff,
  decisionProblem,
  proofImageSrc,
  reviewComponents,
  whatIsWaiting,
} from './reviewLens';
import type { ReviewAsset, ReviewPayload, ReviewSlot } from './reviewLens';
import { ImageLightbox } from '../../components/ImageLightbox';

/**
 * The client-facing review page. Public, unauthenticated, one URL.
 *
 * Everybody else who opens this application has an account, a colleague to
 * ask, and a reason to assume a confusing screen is their own fault. The
 * person here has a link somebody emailed them and no context at all. So:
 * nothing is abbreviated, every state says what it means in words, and no
 * button is offered that the server would refuse.
 *
 * ── What it deliberately does not do ───────────────────────────────────────
 *
 * No sidebar, no shell, no navigation. This route is mounted outside every
 * layout and every auth boundary — a client must never see the inside of TAP's
 * tooling, and a page that renders the shell would leak the shape of it.
 *
 * No optimistic updates. Each decision returns the server's whole new view of
 * the order and the page replaces its state with that. Guessing locally would
 * mean a slot briefly showing approved when the write actually failed — on a
 * page whose entire purpose is recording what somebody agreed to.
 */

const STATUS_LABELS: Record<ReviewSlot['status'], string> = {
  awaiting: 'Not sent yet',
  pending: 'Waiting on you',
  approved: 'Approved',
  revision_requested: 'Revision requested',
  signed: 'Signed off',
};

const STATUS_TONES: Record<ReviewSlot['status'], 'neutral' | 'brand' | 'success' | 'warning'> = {
  awaiting: 'neutral',
  pending: 'brand',
  approved: 'success',
  revision_requested: 'warning',
  signed: 'success',
};

/**
 * One component's proof: the image itself, then the two things somebody wants
 * to do with it.
 *
 * This used to be a text link reading "View proof (version 2) ↗". A client
 * deciding whether to approve artwork had to leave the page they were deciding
 * on, land in Drive, and come back — and the three of four who did not bother
 * were approving something they had not looked at.
 *
 * The <img> is NOT wrapped in a link, deliberately: the lightbox ignores an
 * image inside an anchor, and a click that navigates away is the behaviour
 * being replaced. The link is still there, next to Download, for anyone who
 * wants the original.
 */
function ProofPreview({ asset }: { asset: ReviewAsset }) {
  const src = proofImageSrc(asset);
  const [failed, setFailed] = useState(false);

  return (
    <div className="review-slot-proof-block">
      {src === null || failed ? null : (
        <img
          className="review-slot-image is-zoomable"
          src={src}
          alt={`${asset.component_type} proof, version ${String(asset.version)}`}
          loading="lazy"
          // A thumbnail that 404s leaves an alt-text box where artwork should
          // be, which reads as "TAP sent me a broken file". Falling back to
          // the link is the state this page was in before, and it worked.
          onError={() => setFailed(true)}
        />
      )}
      <div className="review-slot-proof-actions">
        <a className="review-slot-proof" href={asset.url} target="_blank" rel="noopener noreferrer">
          View proof (version {asset.version}) ↗
        </a>
        {/* A real download, not a second view link. `download` is advisory
            across origins, so this is a hint plus the correct target rather
            than a guarantee — but it is what a client means by "send me the
            file". */}
        <a
          className="review-slot-download"
          href={asset.url}
          download
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </a>
      </div>
      {src === null || failed ? null : (
        <p className="review-slot-zoom-hint">Click the image to see it full screen.</p>
      )}
    </div>
  );
}

export function ReviewPortalPage() {
  const { token = '' } = useParams();
  const { client } = useAuth();

  const [payload, setPayload] = useState<ReviewPayload | null>(null);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);

  // Which slot has its rejection box open, and what is in it.
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [signoffError, setSignoffError] = useState('');

  useEffect(() => {
    let live = true;
    loadReview(client, token).then(
      (data) => {
        if (live) setPayload(data);
      },
      (cause: unknown) => {
        if (live) setLoadError(reviewErrorMessage(cause));
      },
    );
    return () => {
      live = false;
    };
  }, [client, token]);

  if (loadError !== '') {
    return (
      <main className="review-page">
        <div className="review-card review-card--dead">
          <h1 className="review-title">This link is no longer active</h1>
          <p>{loadError}</p>
        </div>
      </main>
    );
  }

  if (payload === null) {
    return (
      <main className="review-page">
        <Skeleton lines={6} label="Loading your review" />
      </main>
    );
  }

  const slots = buildSlots(payload.assets, reviewComponents(payload));
  const signed = payload.certificate !== null;
  const showSignoff = canSignOff(payload);

  async function decide(slot: ReviewSlot, decision: 'approved' | 'revision_requested') {
    if (slot.asset === null) return;
    // Asked before the round trip. The server enforces this too, but being
    // told off after a wait is a worse experience than being asked first.
    const problem = decisionProblem(decision, note);
    if (problem !== null) {
      setActionError(problem);
      return;
    }
    setActionError('');
    setBusy(true);
    try {
      const next = await decideComponent(client, {
        token,
        assetId: slot.asset.id,
        decision,
        note: decision === 'approved' ? '' : note,
      });
      setPayload(next);
      setRejecting(null);
      setNote('');
    } catch (cause) {
      setActionError(reviewErrorMessage(cause));
    }
    setBusy(false);
  }

  async function submitSignoff() {
    if (name.trim() === '' || email.trim() === '' || title.trim() === '' || !confirmed) {
      setSignoffError('Please fill in your name, email and title, and tick the confirmation.');
      return;
    }
    setSignoffError('');
    setBusy(true);
    try {
      const certificate = await signOff(client, { token, name, email, title, confirmed });
      setPayload({ ...payload!, certificate });
      // Re-read so the slots show as frozen: issue_signoff_certificate moves
      // every approved asset to final_approved in the same transaction.
      try {
        setPayload(await loadReview(client, token));
      } catch {
        // The certificate exists; a failed refresh is cosmetic.
      }
    } catch (cause) {
      setSignoffError(reviewErrorMessage(cause));
    }
    setBusy(false);
  }

  return (
    <main className="review-page">
      {/* The order number is deliberately not printed. It is ours, not the
          client's: a low number tells a stranger how new this operation is,
          and nothing on this page needs it. It stays in the Hub, where the
          people who need it are. What replaces it is the one fact the client
          does want at a glance, which is whether anything is being asked of
          them, before they scroll two thousand pixels to work it out. */}
      <header className="review-header">
        <img className="review-logo" src="/4ocean-logo-blue.png" alt="4ocean" />
        <h1 className="review-title">4ocean Custom Products Design Approval</h1>
        <p className="review-order">
          <strong>{payload.order.client}</strong>
        </p>
        <p className="review-standing">{whatIsWaiting(slots, signed)}</p>
      </header>

      {/* Alex's own words, near enough. The page used to open cold, straight
          into a component list, which he read as "very AI and very blunt".
          A client opening this has just agreed to spend money with us and
          the first thing they meet should sound like a person. */}
      <section className="review-intro" aria-label="Introduction">
        <p>Thank you so much for your interest in 4ocean, and for helping us clean our oceans.</p>
        <p>
          Below we have created four individual mockups for your approval: your bracelet charm, the
          bracelet cord, and the packaging front and back.
        </p>
        <p>
          Please go through them individually and let us know your thoughts. If a design looks
          right, hit <strong>Approve</strong>. If something needs changing, use{' '}
          <strong>Request changes</strong> and tell us what you would like to see different.
        </p>
        <p>As soon as we have all of this back from you, we will go from there.</p>
      </section>

      {payload.certificate === null ? null : (
        <section className="review-card review-card--signed" aria-label="Sign-off certificate">
          <h2 className="review-card-title">Signed off</h2>
          <p>
            Approved by <strong>{payload.certificate.client_name}</strong>. Keep this certificate
            number for your records.
          </p>
          <p className="review-cert-no">{payload.certificate.certificate_no}</p>
        </section>
      )}

      <section aria-label="Components">
        <h2 className="review-section-title">Your four components</h2>
        <p className="review-hint">
          Approving a component is final for that component. The others stay open, so you can send
          one back without holding up the rest.
        </p>
        {actionError === '' ? null : <InlineError>{actionError}</InlineError>}

        <ul className="review-slots">
          {slots.map((slot) => (
            <li key={slot.component} className="review-slot">
              <div className="review-slot-head">
                <h3 className="review-slot-name">{slot.component}</h3>
                <Badge tone={STATUS_TONES[slot.status]}>{STATUS_LABELS[slot.status]}</Badge>
              </div>

              {slot.asset === null ? (
                <p className="review-slot-empty">
                  This component has not been sent yet. It will appear here.
                </p>
              ) : (
                <>
                  <ProofPreview asset={slot.asset} />

                  {slot.status === 'revision_requested' ? (
                    <>
                      <p className="review-slot-empty">
                        Thanks. We are working on a revision, and it will appear here when it is
                        ready.
                      </p>
                      {slot.note === '' ? null : (
                        /* The client's own words, read back. A slot that says
                           "Revision requested" and nothing else asks somebody
                           to remember what they typed a week ago — and on a
                           second round it is the only thing distinguishing
                           which request is being worked on. */
                        <div className="review-slot-feedback">
                          <p className="review-slot-feedback-label">What you asked for</p>
                          <blockquote className="review-slot-feedback-note">{slot.note}</blockquote>
                        </div>
                      )}
                    </>
                  ) : null}

                  {slot.decidable && !signed ? (
                    rejecting === slot.component ? (
                      <div className="review-slot-reject">
                        <Textarea
                          label="What needs to change?"
                          rows={3}
                          value={note}
                          disabled={busy}
                          onChange={(event) => setNote(event.target.value)}
                        />
                        <div className="review-slot-actions">
                          <Button
                            variant="secondary"
                            disabled={busy}
                            onClick={() => {
                              setRejecting(null);
                              setNote('');
                              setActionError('');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={busy}
                            onClick={() => {
                              void decide(slot, 'revision_requested');
                            }}
                          >
                            Send back for revision
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="review-slot-actions">
                        <Button
                          disabled={busy}
                          onClick={() => {
                            void decide(slot, 'approved');
                          }}
                        >
                          {`Approve ${slot.component}`}
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={busy}
                          onClick={() => {
                            setRejecting(slot.component);
                            setNote('');
                            setActionError('');
                          }}
                        >
                          {`Request changes to ${slot.component}`}
                        </Button>
                      </div>
                    )
                  ) : null}

                  {slot.status === 'approved' && !signed ? (
                    <p className="review-slot-approved">
                      <span aria-hidden="true">✓</span>
                      Approved by you
                    </p>
                  ) : null}
                </>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* The order summary sits BELOW the artwork, and that is the change
          rather than a coincidence of order. This page used to open with a
          price table above the proofs it exists to get a decision on.
          Somebody who came to look at artwork was shown an invoice first,
          which invites a conversation about price at the moment we want one
          about design. */}
      {/* No prices, and that is a sequencing decision rather than a design
          one. The invoice is raised AFTER approval and carries the real
          shipping figure; this page can only show an estimate of it. A
          client who reads a total here and a different total on the invoice
          reads it as a price change, and we have spent their goodwill on a
          number that was never the point of the page.
          Quantity stays. Approving artwork for 500 units is a different
          decision from approving it for 5,000, and that is the one number
          that belongs to this page rather than to the invoice. */}
      <section className="review-card" aria-label="Order summary">
        <h2 className="review-card-title">Your order detail summary</h2>
        <table className="review-items">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Quantity</th>
            </tr>
          </thead>
          <tbody>
            {payload.items.map((item) => (
              <tr key={item.id}>
                <td>
                  {item.product_type}
                  {item.description === '' ? null : (
                    <span className="review-item-desc">{item.description}</span>
                  )}
                </td>
                <td>{Number(item.units)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {showSignoff ? (
        <section className="review-card" aria-label="Sign off">
          <h2 className="review-card-title">Sign off on this order</h2>
          <p className="review-hint">
            All four components are approved. Signing off confirms the designs above are final and
            releases the order into production.
          </p>
          {signoffError === '' ? null : <InlineError>{signoffError}</InlineError>}
          <Input
            label="Your full name"
            value={name}
            disabled={busy}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label="Your email"
            type="email"
            value={email}
            disabled={busy}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="Your title"
            value={title}
            disabled={busy}
            onChange={(event) => setTitle(event.target.value)}
          />
          <Checkbox
            label="I confirm I am authorised to approve these designs on behalf of my company."
            checked={confirmed}
            disabled={busy}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <div className="review-slot-actions">
            <Button
              disabled={busy}
              onClick={() => {
                void submitSignoff();
              }}
            >
              Sign off on this order
            </Button>
          </div>
        </section>
      ) : signed || !allApproved(slots) ? null : null}

      {/* One line, and no message box. These links are sent inside an email
          thread the client is already in, so a form on the page invites them
          to start a second conversation somewhere nobody is watching. No
          address is printed either: "the thread that brought you here"
          reaches the person who actually sent it, survives a change of
          staff, and puts nothing on a public page for a scraper. */}
      <footer className="review-footer">
        <p>
          Questions about your designs? Just reply to the email thread that brought you here and
          your 4ocean contact will pick it up.
        </p>
      </footer>

      {/* Mounted here rather than inherited. Every other screen gets the
          lightbox from the app shell; this page renders outside it, which is
          exactly why proofs could never be inspected full screen before. */}
      <ImageLightbox />
    </main>
  );
}
